import find from 'lodash/find';
import { computed, getCurrentInstance } from 'vue';

import AreasOfInterestType from '@/enums/AreasOfInterestType';
import Designation from '@/enums/Designation';
import { i18n } from '@/i18n';

export default function useSearchData(isRecommended = false) {
  const root = getCurrentInstance().proxy.$root;
  const rootGetters = root.$store.getters;

  const getDesignationOptions = [
    {
      value: Designation.Veterinarian,
      text: i18n.t('search.vetster-medical'),
      subtext: i18n.t('search.veterinarians'),
    },
    {
      value: Designation.Technician,
      text: i18n.t('search.vetster-wellness'),
      subtext: i18n.t('search.veterinary-technicians'),
    },
  ];

  const getLanguageOptions = [
    {
      value: 'en',
      text: 'English',
    },
    {
      value: 'fr',
      text: 'French',
    },
    {
      value: 'es',
      text: 'Spanish',
    },
  ];

  const getAreasOfInterestOptions = AreasOfInterestType.values().map((value) => ({
    value,
    text: i18n.t(`search.areas-of-interest-type.${value}`),
  }));

  const searchDesignation = (search) => find(getDesignationOptions, { value: search.designation })?.value
    || Designation.Veterinarian;

  const validSearchData = (search = {}) => Object.assign(search, {
    designation: searchDesignation(search),
  });

  const subscriptionBannerPartnerRateThreshold = computed(() => {
    // Handle the Statsig client failing to initialize, using fallback value $100.
    if (!root.$statsig.ready) {
      return 10000;
    }

    const priceCapConfig = root.$statsig.getDynamicConfig('subs_price_gating')?.value;

    return priceCapConfig?.guardrails?.find((guardrail) => guardrail.program === 'Vetster Plus')?.booking;
  });

  const showSubscriptionBannerForPartner = (timeslots) => timeslots?.some((timeslot) => Number(timeslot.rate.amount) < subscriptionBannerPartnerRateThreshold.value);

  const RecommendVetExperimentGroup = {
    1: 'banner',
    2: 'recommend-a-vet',
  };

  const recommendVetExperiment = computed(() => {
    if (!root.$statsig.ready || !root.$statsig.checkGate('search_recommend_a_vet') || root.$store.getters['search/getHideRecommendVet']) {
      return null;
    }

    if (isRecommended) {
      return RecommendVetExperimentGroup[2];
    }

    /** Experiment a
     1 — v2a — Control — Normal guided search flow
     2 — v2a — Variant 1 — Banner at bottom results
     3 — v2a — Variant 2 — Recommend a vet flow
     * */

    /** Experiment b
     1 — v2b — Control — Normal guided search flow
     2 — v2b — Variant 2 — Recommend a vet flow
     * */

    const group = root.$statsig.getExperiment('expt_search_recommend_a_vet_v2a').get('value')
      ?? root.$statsig.getExperiment('expt_search_recommend_a_vet_v2b').get('value');

    return RecommendVetExperimentGroup[group] ?? null;
  });

  const isFlatRateBookingPaygPricingExperiment = computed(() => {
    const flatRateStates = ['NY', 'FL', 'CA'];
    const searchInputs = rootGetters['search/getInputs'];

    return searchInputs.country.toUpperCase() === 'US'
      && flatRateStates.includes(searchInputs.province)
      && root.$statsig.checkGate('booking_flat_payg_pricing')
      && root.$statsig.getExperiment('expt_booking_flat_payg_pricing').get('value') > 0;
  });

  const isSingleConcernSelect = computed(() => root.$statsig.ready && root.$statsig.checkGate('search_single_concern_select'));

  const searchConcernLimit = computed(() => (isSingleConcernSelect.value ? 1 : 3));

  const isRecommendVetBannerExperiment = computed(() => recommendVetExperiment.value === RecommendVetExperimentGroup[1]);
  const isRecommendVetExperiment = computed(() => recommendVetExperiment.value === RecommendVetExperimentGroup[2]);

  return {
    getAreasOfInterestOptions,
    getDesignationOptions,
    getLanguageOptions,
    isFlatRateBookingPaygPricingExperiment,
    isRecommendVetBannerExperiment,
    isRecommendVetExperiment,
    searchConcernLimit,
    showSubscriptionBannerForPartner,
    validSearchData,
  };
}
